import { inject, Injectable } from '@angular/core';
import {
  CONFIGURATION_SETTING_KEYS,
  AdditionalEntryValueType,
  EDirectSaleAlternatePassengerData,
  IAdditionalEntryValue,
  ITariffAdditionalEntry,
  isDateAdditionalEntry,
  TariffProductDefinition,
  TariffProductGroup,
  TariffSelectionAttribute,
  TariffSelectionAttributeValue,
  UserDto,
} from '@web/dtos';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ITicketPersonalDataForm } from '../../pages/shop/direct-ticket-configuration-page/direct-ticket-personal-data-page/direct-ticket-personal-data-page.component';
import {
  ICompanionDataForm,
  IRequiredTicketOwnerData,
  ITicketOwnerDataForm,
} from '../../pages/shop/direct-ticket-configuration-page/direct-ticket-owner-data-page/direct-ticket-owner-data-page.component';
import { ConfigurationStore } from '../api/configuration/configuration-store.service';

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 10;

export interface ITicketDefinitionForm {
  tariffGroup: FormControl<TariffProductGroup | undefined>;
  selectionAttributes: FormArray<FormControl<TariffSelectionAttributeValue>>;
  productSortOptions: FormArray<FormControl<TariffSelectionAttributeValue>>;
  additionalEntries: FormArray<FormGroup<IAdditionalEntryForm>>;
  product: FormControl<TariffProductDefinition | undefined>;
  amount: FormControl<number>;
  amountOfPeople: FormControl<number>;
  travelDate: FormControl<Date | null | undefined>;
}

export interface IAdditionalEntryForm {
  externalReference: FormControl<string | null>;
  name: FormControl<string | null>;
  value: FormControl<AdditionalEntryValueType>;
}

export interface ITicketOwnerMandatoryFields {
  isAlternatePassengerDataSalutationRequired: boolean;
  isAlternatePassengerDataDateOfBirthRequired: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartFormService {
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly formBuilder: FormBuilder = inject(FormBuilder);

  public readonly minAmount: number = MIN_AMOUNT;
  public readonly maxAmount: number = MAX_AMOUNT;

  public createProductForm(
    selectionAttributes: TariffSelectionAttribute[],
    productSortOptions: TariffSelectionAttribute[],
    ticketConfiguration?: FormGroup<ITicketDefinitionForm>['value']
  ): FormGroup<ITicketDefinitionForm> {
    const product: TariffProductDefinition | undefined =
      ticketConfiguration?.product;
    const minimumNumberOfPeople: number =
      this.getMinimumNumberOfPeople(product);
    const maximumNumberOfPeople: number =
      this.getMaximumNumberOfPeople(product);
    const amountOfPeople: number = this.clampAmountOfPeople(
      ticketConfiguration?.amountOfPeople ??
        product?.standardNumberOfPeople ??
        product?.numberOfPeople ??
        minimumNumberOfPeople,
      minimumNumberOfPeople,
      maximumNumberOfPeople
    );

    const productForm: FormGroup<ITicketDefinitionForm> =
      this.formBuilder.group<ITicketDefinitionForm>({
        tariffGroup: this.formBuilder.nonNullable.control(
          ticketConfiguration?.tariffGroup,
          [Validators.required]
        ),
        product: this.formBuilder.nonNullable.control(
          ticketConfiguration?.product,
          [Validators.required]
        ),
        selectionAttributes: this.formBuilder.nonNullable.array(
          selectionAttributes.map(
            (value: TariffSelectionAttribute, index: number) => {
              return this.formBuilder.nonNullable.control(
                ticketConfiguration?.selectionAttributes?.[index] ??
                  value.values[0],
                [Validators.required]
              );
            }
          )
        ),
        productSortOptions: this.formBuilder.nonNullable.array(
          productSortOptions.map(
            (value: TariffSelectionAttribute, index: number) => {
              return this.formBuilder.nonNullable.control(
                ticketConfiguration?.productSortOptions?.[index] ??
                  ticketConfiguration?.product?.keyElements.find(
                    element =>
                      element.name.toLowerCase() === value.name.toLowerCase()
                  )?.value ??
                  value.values[0],
                [Validators.required]
              );
            }
          )
        ),
        additionalEntries: this.formBuilder.array<
          FormGroup<IAdditionalEntryForm>
        >([]),
        travelDate: this.formBuilder.nonNullable.control(
          ticketConfiguration?.travelDate ?? new Date(),
          [Validators.required]
        ),
        amount: this.formBuilder.nonNullable.control(
          ticketConfiguration?.amount ?? this.minAmount,
          [
            Validators.required,
            Validators.min(this.minAmount),
            Validators.max(this.maxAmount),
          ]
        ),
        amountOfPeople: this.formBuilder.nonNullable.control(amountOfPeople, [
          Validators.required,
          Validators.min(minimumNumberOfPeople),
          Validators.max(maximumNumberOfPeople),
        ]),
      });

    this.updateAdditionalEntries(
      productForm,
      product,
      (ticketConfiguration?.additionalEntries ?? []).map(
        (entry): IAdditionalEntryValue => ({
          externalReference: entry.externalReference ?? null,
          name: entry.name ?? null,
          value: entry.value ?? null,
        })
      )
    );

    return productForm;
  }

  public updateAdditionalEntries(
    productForm: FormGroup<ITicketDefinitionForm>,
    product: TariffProductDefinition | undefined,
    existingValues: IAdditionalEntryValue[] = []
  ): void {
    const additionalEntries: FormArray<FormGroup<IAdditionalEntryForm>> =
      productForm.controls.additionalEntries;
    const definitions: ITariffAdditionalEntry[] = [
      ...(product?.sort.additionalEntries ?? []),
    ].sort(
      (a: ITariffAdditionalEntry, b: ITariffAdditionalEntry) =>
        a.sortOrder - b.sortOrder
    );

    additionalEntries.clear({ emitEvent: false });
    definitions.forEach((entry: ITariffAdditionalEntry, index: number) => {
      const existingValue: IAdditionalEntryValue | undefined =
        existingValues.find(
          (value: IAdditionalEntryValue) =>
            value.externalReference === entry.externalReference &&
            value.name === entry.name
        ) ?? existingValues[index];

      additionalEntries.push(
        this.createAdditionalEntryForm(entry, existingValue?.value),
        { emitEvent: false }
      );
    });
    additionalEntries.updateValueAndValidity();
  }

  private createAdditionalEntryForm(
    entry: ITariffAdditionalEntry,
    existingValue?: AdditionalEntryValueType
  ): FormGroup<IAdditionalEntryForm> {
    return this.formBuilder.group<IAdditionalEntryForm>({
      externalReference: this.formBuilder.control<string | null>(
        entry.externalReference
      ),
      name: this.formBuilder.control<string | null>(entry.name),
      value: this.formBuilder.control<AdditionalEntryValueType>(
        this.normalizeAdditionalEntryValue(entry, existingValue),
        this.createAdditionalEntryValidators(entry)
      ),
    });
  }

  private normalizeAdditionalEntryValue(
    entry: ITariffAdditionalEntry,
    value?: AdditionalEntryValueType
  ): AdditionalEntryValueType {
    if (!isDateAdditionalEntry(entry)) {
      return value ?? '';
    }
    if (value instanceof Date || value === null) {
      return value;
    }
    if (typeof value === 'string' && value) {
      const date: Date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  private createAdditionalEntryValidators(
    entry: ITariffAdditionalEntry
  ): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (!entry.optional) {
      validators.push(Validators.required);
    }
    if (entry.dataType === 'Zahl') {
      if (entry.minValue !== null) {
        validators.push(Validators.min(entry.minValue));
      }
      if (entry.maxValue !== null) {
        validators.push(Validators.max(entry.maxValue));
      }
    }
    if (entry.dataType === 'Nummer') {
      validators.push(Validators.pattern(/^\d+$/));
    }
    if (this.isTimeAdditionalEntry(entry)) {
      validators.push(Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/));
    }
    if (!isDateAdditionalEntry(entry) && !this.isTimeAdditionalEntry(entry)) {
      if (entry.minLength !== null) {
        validators.push(Validators.minLength(entry.minLength));
      }
      if (entry.maxLength !== null) {
        validators.push(Validators.maxLength(entry.maxLength));
      }
    }
    return validators;
  }
  private isTimeAdditionalEntry(entry: ITariffAdditionalEntry): boolean {
    const dataType: string = entry.additionalEntryDataType || entry.dataType;
    return dataType === 'Uhrzeit';
  }

  public updateAmountOfPeople(
    productForm: FormGroup<ITicketDefinitionForm>,
    product: TariffProductDefinition | undefined,
    preserveCurrentValue: boolean = true
  ): void {
    const minimumNumberOfPeople: number =
      this.getMinimumNumberOfPeople(product);
    const maximumNumberOfPeople: number =
      this.getMaximumNumberOfPeople(product);
    const currentAmountOfPeople: number = preserveCurrentValue
      ? productForm.controls.amountOfPeople.value
      : (product?.standardNumberOfPeople ??
        product?.numberOfPeople ??
        minimumNumberOfPeople);
    const amountOfPeople: number = this.clampAmountOfPeople(
      currentAmountOfPeople ??
        product?.standardNumberOfPeople ??
        product?.numberOfPeople ??
        minimumNumberOfPeople,
      minimumNumberOfPeople,
      maximumNumberOfPeople
    );

    productForm.controls.amountOfPeople.setValidators([
      Validators.required,
      Validators.min(minimumNumberOfPeople),
      Validators.max(maximumNumberOfPeople),
    ]);
    productForm.controls.amountOfPeople.setValue(amountOfPeople);
  }

  public createPersonalDataForm(): FormGroup<ITicketPersonalDataForm> {
    return this.formBuilder.group<ITicketPersonalDataForm>({
      salutation: this.formBuilder.nonNullable.control('', [
        Validators.required,
      ]),
      firstName: this.formBuilder.nonNullable.control('', [
        Validators.required,
      ]),
      lastName: this.formBuilder.nonNullable.control('', [Validators.required]),
      dateOfBirth: this.formBuilder.nonNullable.control(undefined, [
        Validators.required,
      ]),
      email: this.formBuilder.nonNullable.control('', [
        Validators.required,
        Validators.email,
      ]),
      agreement: this.formBuilder.nonNullable.control(false, [
        Validators.required,
        Validators.requiredTrue,
      ]),
    });
  }

  public createTicketOwnerDataForms(): FormArray<
    FormGroup<ITicketOwnerDataForm>
  > {
    return this.formBuilder.nonNullable.array<FormGroup<ITicketOwnerDataForm>>(
      []
    );
  }

  public createTicketOwnerForm(
    mandatoryFields: ITicketOwnerMandatoryFields,
    amount: number,
    data?: IRequiredTicketOwnerData,
    amountOfPeople: number = 1
  ): FormArray<FormGroup<ITicketOwnerDataForm>> {
    const array: FormArray<FormGroup<ITicketOwnerDataForm>> =
      this.formBuilder.nonNullable.array<FormGroup<ITicketOwnerDataForm>>([]);
    for (let i = 0; i < amount; i++) {
      array.push(
        this.formBuilder.group<ITicketOwnerDataForm>({
          isForUser: this.formBuilder.nonNullable.control(true),
          fellowTravelerId: this.formBuilder.nonNullable.control<
            string | undefined
          >(undefined),
          salutation: this.formBuilder.nonNullable.control(
            data?.salutation ?? '',
            mandatoryFields.isAlternatePassengerDataSalutationRequired
              ? Validators.required
              : []
          ),
          firstName: this.formBuilder.nonNullable.control(
            data?.firstName ?? '',
            Validators.required
          ),
          lastName: this.formBuilder.nonNullable.control(
            data?.lastName ?? '',
            Validators.required
          ),
          dateOfBirth: this.formBuilder.nonNullable.control(
            data?.dateOfBirth ?? undefined,
            mandatoryFields.isAlternatePassengerDataDateOfBirthRequired
              ? Validators.required
              : []
          ),
          companions: this.createCompanionForms(
            amountOfPeople - 1,
            data?.companions
          ),
        })
      );
    }
    return array;
  }

  public createTicketOwnerForms(
    owners: IRequiredTicketOwnerData[],
    mandatoryFields: ITicketOwnerMandatoryFields = this.getTicketOwnerMandatoryFields()
  ): FormArray<FormGroup<ITicketOwnerDataForm>> {
    const array: FormArray<FormGroup<ITicketOwnerDataForm>> =
      this.formBuilder.nonNullable.array<FormGroup<ITicketOwnerDataForm>>([]);
    owners.forEach(data => {
      array.push(
        this.formBuilder.group<ITicketOwnerDataForm>({
          isForUser: this.formBuilder.nonNullable.control(true),
          fellowTravelerId: this.formBuilder.nonNullable.control<
            string | undefined
          >(undefined),
          salutation: this.formBuilder.nonNullable.control(
            data?.salutation ?? '',
            mandatoryFields.isAlternatePassengerDataSalutationRequired
              ? Validators.required
              : []
          ),
          firstName: this.formBuilder.nonNullable.control(
            data?.firstName ?? '',
            Validators.required
          ),
          lastName: this.formBuilder.nonNullable.control(
            data?.lastName ?? '',
            Validators.required
          ),
          dateOfBirth: this.formBuilder.nonNullable.control(
            data?.dateOfBirth ?? undefined,
            mandatoryFields.isAlternatePassengerDataDateOfBirthRequired
              ? Validators.required
              : []
          ),
          companions: this.createCompanionForms(
            data.companions?.length ?? 0,
            data.companions
          ),
        })
      );
    });
    return array;
  }

  public adjustNumberOfTicketOwnerForms(
    ticketOwnerDataForm: FormArray<FormGroup<ITicketOwnerDataForm>>,
    amount: number
  ): void {
    if (ticketOwnerDataForm) {
      if (amount < ticketOwnerDataForm.length) {
        ticketOwnerDataForm.removeAt(ticketOwnerDataForm.length - 1);
      } else if (amount > ticketOwnerDataForm.length) {
        const difference: number = amount - ticketOwnerDataForm.length;
        ticketOwnerDataForm.push(
          this.createTicketOwnerForm(
            this.getTicketOwnerMandatoryFields(),
            difference
          ).controls[0]
        );
      }
    }
    ticketOwnerDataForm.updateValueAndValidity();
  }

  public addTicketOwnerDataForm(
    ticketOwnerDateForms: FormArray<FormArray<FormGroup<ITicketOwnerDataForm>>>,
    amount: number
  ): void {
    ticketOwnerDateForms.controls.push(
      this.createTicketOwnerForm(this.getTicketOwnerMandatoryFields(), amount)
    );
  }

  public adjustNumberOfCompanionForms(
    ticketOwnerDataForm: FormArray<FormGroup<ITicketOwnerDataForm>>,
    amountOfPeople: number
  ): void {
    const amountOfCompanions: number = Math.max(0, amountOfPeople - 1);

    ticketOwnerDataForm.controls.forEach(ownerForm => {
      const companions: FormArray<FormGroup<ICompanionDataForm>> =
        ownerForm.controls.companions;

      while (companions.length > amountOfCompanions) {
        companions.removeAt(companions.length - 1);
      }
      while (companions.length < amountOfCompanions) {
        companions.push(this.createCompanionForm());
      }

      companions.updateValueAndValidity();
    });

    ticketOwnerDataForm.updateValueAndValidity();
  }

  private createCompanionForms(
    amount: number,
    data: IRequiredTicketOwnerData['companions'] = []
  ): FormArray<FormGroup<ICompanionDataForm>> {
    const companions: FormArray<FormGroup<ICompanionDataForm>> =
      this.formBuilder.nonNullable.array<FormGroup<ICompanionDataForm>>([]);

    for (let index = 0; index < Math.max(0, amount); index++) {
      companions.push(this.createCompanionForm(data?.[index]));
    }

    return companions;
  }

  private createCompanionForm(
    data?: NonNullable<IRequiredTicketOwnerData['companions']>[number]
  ): FormGroup<ICompanionDataForm> {
    return this.formBuilder.group<ICompanionDataForm>({
      fellowTravelerId: this.formBuilder.nonNullable.control<
        string | undefined
      >(undefined),
      firstName: this.formBuilder.nonNullable.control(data?.firstName ?? '', [
        Validators.required,
      ]),
      lastName: this.formBuilder.nonNullable.control(data?.lastName ?? '', [
        Validators.required,
      ]),
    });
  }

  private getTicketOwnerMandatoryFields(): ITicketOwnerMandatoryFields {
    return {
      isAlternatePassengerDataSalutationRequired:
        this.configurationStore.hasMultiSelectConfigurationValue(
          CONFIGURATION_SETTING_KEYS.DirectSaleAlternatePassengerData,
          EDirectSaleAlternatePassengerData.Salutation
        ),
      isAlternatePassengerDataDateOfBirthRequired:
        this.configurationStore.hasMultiSelectConfigurationValue(
          CONFIGURATION_SETTING_KEYS.DirectSaleAlternatePassengerData,
          EDirectSaleAlternatePassengerData.DateOfBirth
        ),
    };
  }

  public prefillPersonalData(
    personalDataForm: FormGroup<ITicketPersonalDataForm>,
    userData: UserDto
  ): void {
    personalDataForm.patchValue({
      salutation: userData.salutation,
      firstName: userData.firstName,
      lastName: userData.lastName,
      dateOfBirth: new Date(userData.birthDate),
      email: userData.email,
    });
    personalDataForm.controls.salutation.disable();
    personalDataForm.controls.firstName.disable();
    personalDataForm.controls.lastName.disable();
    personalDataForm.controls.dateOfBirth.disable();
    personalDataForm.controls.email.disable();
  }

  public setCurrentProductFormValuesAndValidators(
    productForm: FormGroup<ITicketDefinitionForm>,
    product: TariffProductDefinition | undefined,
    selectionAttributes: TariffSelectionAttributeValue[],
    productSortOptions: TariffSelectionAttributeValue[],
    travelDate?: Date,
    amount?: number
  ): void {
    productForm.controls.selectionAttributes.clear();
    selectionAttributes.forEach(value => {
      productForm.controls.selectionAttributes.push(
        this.formBuilder.nonNullable.control(value, [Validators.required])
      );
    });
    productForm.controls.productSortOptions.clear();
    productSortOptions.forEach(value => {
      productForm.controls.productSortOptions.push(
        this.formBuilder.nonNullable.control(value, [Validators.required])
      );
    });
    const existingAdditionalEntries: IAdditionalEntryValue[] =
      productForm.controls.additionalEntries.getRawValue();
    this.updateAdditionalEntries(
      productForm,
      product,
      existingAdditionalEntries
    );
    productForm.setValue({
      tariffGroup: productForm.controls.tariffGroup.value,
      product: product,
      selectionAttributes: selectionAttributes,
      productSortOptions: productSortOptions,
      additionalEntries: productForm.controls.additionalEntries.getRawValue(),
      travelDate:
        travelDate ??
        (product?.validityOptions?.earliestValidStart
          ? new Date(product.validityOptions.earliestValidStart)
          : new Date()),
      amount: amount ?? this.minAmount,
      amountOfPeople:
        product?.standardNumberOfPeople ??
        product?.numberOfPeople ??
        this.getMinimumNumberOfPeople(product),
    });
    productForm.controls.travelDate.setValidators([Validators.required]);
    productForm.controls.amount.setValidators([
      Validators.required,
      Validators.min(this.minAmount),
      Validators.max(this.maxAmount),
    ]);
    this.updateAmountOfPeople(productForm, product);
    productForm.updateValueAndValidity();
  }

  private getMinimumNumberOfPeople(
    product: TariffProductDefinition | undefined
  ): number {
    return product?.minNumberOfPeople ?? product?.numberOfPeople ?? 1;
  }

  private getMaximumNumberOfPeople(
    product: TariffProductDefinition | undefined
  ): number {
    return (
      product?.maxNumberOfPeople ??
      product?.numberOfPeople ??
      this.getMinimumNumberOfPeople(product)
    );
  }

  private clampAmountOfPeople(
    amountOfPeople: number,
    minimumNumberOfPeople: number,
    maximumNumberOfPeople: number
  ): number {
    return Math.min(
      maximumNumberOfPeople,
      Math.max(minimumNumberOfPeople, amountOfPeople)
    );
  }
}
